import { create } from 'zustand'
import { remotePlayers } from '../api/client'
import { usePlayerStore } from './player'

export interface VideoItem {
  id: string
  title: string
  seriesTitle?: string
  seasonNumber?: number
  episodeNumber?: number
  thumbnailPath?: string
  durationMs?: number
  streamUrl: string
  mediaType?: 'movie' | 'episode' | 'youtube'
}

export type VideoQuality = 'original' | '1080p' | '720p' | '480p' | '360p'

export const QUALITY_OPTIONS: { value: VideoQuality; label: string; description: string }[] = [
  { value: 'original', label: 'Original', description: 'Full quality' },
  { value: '1080p', label: '1080p', description: '~8 Mbps' },
  { value: '720p', label: '720p', description: '~5 Mbps' },
  { value: '480p', label: '480p', description: '~2.5 Mbps' },
  { value: '360p', label: '360p', description: '~1 Mbps' },
]

interface VideoPlayerState {
  // Current video
  currentVideo: VideoItem | null
  isPlaying: boolean
  progress: number // 0-100
  duration: number // in seconds
  currentTime: number // in seconds
  buffered: number // in seconds
  volume: number // 0-1
  isMuted: boolean
  isFullscreen: boolean
  quality: VideoQuality

  // Video element ref (set by VideoPlayer component)
  videoRef: HTMLVideoElement | null

  // Actions
  playVideo: (video: VideoItem) => void
  pause: () => void
  resume: () => void
  seek: (percent: number) => void
  setVolume: (volume: number) => void
  toggleMute: () => void
  setVideoRef: (ref: HTMLVideoElement | null) => void
  updateProgress: (currentTime: number, duration: number, buffered: number) => void
  setIsPlaying: (isPlaying: boolean) => void
  setFullscreen: (isFullscreen: boolean) => void
  stopVideo: () => void
  setQuality: (quality: VideoQuality) => void
}

// Load persisted volume from localStorage
function getPersistedVolume(): number {
  try {
    const stored = localStorage.getItem('s3nd-video-volume')
    if (stored) {
      const vol = parseFloat(stored)
      if (!isNaN(vol) && vol >= 0 && vol <= 1) return vol
    }
  } catch {}
  return 1.0 // Default volume
}

// Load persisted quality from localStorage
function getPersistedQuality(): VideoQuality {
  try {
    const stored = localStorage.getItem('s3nd-video-quality')
    if (stored && ['original', '1080p', '720p', '480p', '360p'].includes(stored)) {
      return stored as VideoQuality
    }
  } catch {}
  return 'original' // Default quality
}

export const useVideoPlayerStore = create<VideoPlayerState>((set, get) => ({
  currentVideo: null,
  isPlaying: false,
  progress: 0,
  duration: 0,
  currentTime: 0,
  buffered: 0,
  volume: getPersistedVolume(),
  isMuted: false,
  isFullscreen: false,
  quality: getPersistedQuality(),
  videoRef: null,

  playVideo: (video) => {
    const remotePlayerId = usePlayerStore.getState().remotePlayerId
    if (remotePlayerId) {
      const { videoRef } = get()
      if (videoRef) {
        videoRef.pause()
        videoRef.removeAttribute('src')
        videoRef.load()
      }
      remotePlayers.command(remotePlayerId, {
        kind: 'play',
        media_type: video.mediaType || 'movie',
        media_id: video.id,
        title: video.title,
      }).then(() => usePlayerStore.getState().refreshRemoteStatus()).catch(console.error)
      set({
        currentVideo: video,
        isPlaying: true,
        progress: 0,
        currentTime: 0,
        duration: (video.durationMs || 0) / 1000,
      })
      return
    }

    usePlayerStore.getState().pause()

    const baseUrl = video.mediaType === 'youtube'
      ? video.streamUrl
      : `/api/stream/video/${video.id}/master.m3u8`

    set({
      currentVideo: { ...video, streamUrl: baseUrl },
      isPlaying: true,
      progress: 0,
      currentTime: 0,
      duration: (video.durationMs || 0) / 1000,
    })
  },

  pause: () => {
    const { videoRef } = get()
    if (videoRef) {
      videoRef.pause()
    }
    set({ isPlaying: false })
  },

  resume: () => {
    const { videoRef } = get()
    if (videoRef) {
      videoRef.play()
    }
    set({ isPlaying: true })
  },

  seek: (percent) => {
    const { videoRef, duration } = get()
    if (videoRef && duration > 0) {
      const newTime = (percent / 100) * duration
      videoRef.currentTime = newTime
      set({ progress: percent, currentTime: newTime })
    }
  },

  setVolume: (volume) => {
    const { videoRef } = get()
    if (videoRef) {
      videoRef.volume = volume
    }
    // Persist volume
    try {
      localStorage.setItem('s3nd-video-volume', volume.toString())
    } catch {}
    set({ volume, isMuted: volume === 0 })
  },

  toggleMute: () => {
    const { videoRef, isMuted } = get()
    if (videoRef) {
      videoRef.muted = !isMuted
    }
    set({ isMuted: !isMuted })
  },

  setVideoRef: (ref) => {
    const { currentVideo, volume } = get()
    set({ videoRef: ref })

    // If there's a current video and we just got a ref, load it locally only
    // when the browser is the active output. A selected Pi should receive
    // commands without the web page also starting local playback.
    if (ref && currentVideo && !usePlayerStore.getState().remotePlayerId) {
      ref.src = currentVideo.streamUrl
      ref.volume = volume
    }
  },

  updateProgress: (currentTime, duration, buffered) => {
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0
    set({ currentTime, duration, buffered, progress })
  },

  setIsPlaying: (isPlaying) => {
    set({ isPlaying })
  },

  setFullscreen: (isFullscreen) => {
    set({ isFullscreen })
  },

  stopVideo: () => {
    const { videoRef } = get()
    if (videoRef) {
      videoRef.pause()
      videoRef.src = ''
    }
    set({
      currentVideo: null,
      isPlaying: false,
      progress: 0,
      currentTime: 0,
      duration: 0,
      buffered: 0,
    })
  },

  setQuality: (quality) => {
    try {
      localStorage.setItem('s3nd-video-quality', quality)
    } catch {}

    set({ quality })
  },
}))
